import {
  clearAuthTokens,
  getAccessToken,
  getRefreshToken,
  setAuthTokens,
} from "@/lib/auth-storage";

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";
let refreshRequest: Promise<boolean> | null = null;

type ApiRequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  auth?: boolean;
  retryOnUnauthorized?: boolean;
  headers?: Record<string, string>;
  credentials?: RequestCredentials;
};

async function tryRefreshToken() {
  if (refreshRequest) {
    return refreshRequest;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return false;
  }

  refreshRequest = (async () => {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      clearAuthTokens();
      return false;
    }

    const json = await response.json();
    if (!json?.accessToken || !json?.refreshToken) {
      clearAuthTokens();
      return false;
    }

    setAuthTokens({
      accessToken: json.accessToken,
      refreshToken: json.refreshToken,
    });

    return true;
  })();

  try {
    return await refreshRequest;
  } finally {
    refreshRequest = null;
  }
}

export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const {
    method = "GET",
    body,
    auth = true,
    retryOnUnauthorized = true,
    headers = {},
    credentials,
  } = options;

  const requestHeaders: Record<string, string> = {
    ...headers,
  };

  if (body !== undefined) {
    requestHeaders["Content-Type"] = "application/json";
  }

  if (auth) {
    const token = getAccessToken();
    if (token) {
      requestHeaders.Authorization = `Bearer ${token}`;
    }
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers: requestHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials,
  });

  if (response.status === 401 && auth && retryOnUnauthorized) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      return apiRequest<T>(path, {
        ...options,
        retryOnUnauthorized: false,
      });
    }
  }

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const payload = await response.json();
      if (payload?.code === "SUBSCRIPTION_EXPIRED") {
        window.dispatchEvent(new CustomEvent("workspace-subscription-expired"));
      }
      message = payload?.message ?? payload?.error ?? message;
    } catch {
      // no-op
    }
    throw new Error(Array.isArray(message) ? message.join(", ") : message);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}
